import { Options } from '@contentful/rich-text-react-renderer';
import { BLOCKS, INLINES } from '@contentful/rich-text-types';
import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  AccordionProps,
  Grid,
  GridItem,
  Text,
} from '@everlywell/ui-kit';
import { Link } from 'gatsby-link';
import { GatsbyImage as Img } from 'gatsby-plugin-image';
import React from 'react';
import analytics from 'utils/analytics';
import { ANALYTICS } from 'utils/constants/analytics';
import renderRichText, {
  ContentfulRichText,
} from 'utils/contentful/renderRichText';
import { FAQCase } from 'utils/types';

export type FaqItem = {
  question: string;
  answer: ContentfulRichText;
  cases?: FAQCase[];
};

export type FaqAccordionProps = {
  faqs: FaqItem[];
  width?: AccordionProps['width'];
  // The tracking label for when the accordion is clicked. Default is 'FaqV2'.
  trackingLabel?: string;
  // When true, the accordion button will have the default size and not wrapper in a Text component. Default is false.
  useAccordionButtonTextElement?: boolean;
};

const options: Options = {
  renderNode: {
    [BLOCKS.PARAGRAPH]: (_, children) => <p>{children}</p>,
    [INLINES.HYPERLINK]: (node, children) => (
      <Link to={node.data.uri} style={{ textDecoration: 'underline' }}>
        {children}
      </Link>
    ),
    [BLOCKS.UL_LIST]: (_, children) => (
      <ul style={{ listStyleType: 'disc', marginInlineStart: '1em' }}>
        {children}
      </ul>
    ),
    [BLOCKS.OL_LIST]: (_, children) => (
      <ol style={{ listStyleType: 'decimal', marginInlineStart: '1em' }}>
        {children}
      </ol>
    ),
  },
};

/**
 * Renders the FaqAccordion component. A list of accordions with rich text content.
 * @param props [FaqAccordionProps]
 * @param props.faqs [FaqItem[]] List of FAQ items
 * @param props.faqs[].question [string] The question
 * @param props.faqs[].answer [ContentfulRichText] The answer
 * @param props.faqs[].cases [FAQCase[]] List of FAQ cases
 *
 * @returns JSX.Element
 */
export default function FaqAccordion({
  faqs,
  width = '100%',
  trackingLabel = 'FaqV2',
  useAccordionButtonTextElement = true,
}: FaqAccordionProps): JSX.Element {
  const handleQuestionOpen = (question: string) => {
    analytics.track({
      event: ANALYTICS.EVENTS.FAQ_CLICK,
      data: { question: question, contentfulSection: trackingLabel },
    });
  };

  return (
    <Accordion allowMultiple width={width} maxWidth="100%">
      {faqs.map((faq, index) => (
        <AccordionItem key={index}>
          <AccordionButton
            onClick={() => handleQuestionOpen(faq.question)}
            sx={{ fontWeight: 'medium' }}
          >
            {useAccordionButtonTextElement ? (
              <Text size="xl" fontWeight="medium">
                {faq.question}
              </Text>
            ) : (
              faq.question
            )}
            <AccordionIcon />
          </AccordionButton>
          <AccordionPanel
            fontWeight="normal"
            paddingLeft="1"
            sx={{
              '&>*:not(:last-child)': {
                marginBottom: 4,
              },
            }}
          >
            {renderRichText(faq.answer, options)}
            {faq?.cases?.length ? (
              <Grid columns={[1, 2, 3]} marginTop={8}>
                {faq?.cases?.length &&
                  faq.cases.map((item, i): any => (
                    <GridItem key={i}>
                      <Img
                        image={item.image.gatsbyImageData}
                        alt={item.image.description || item.title}
                      />
                      <Text
                        size="lg"
                        color="tints.black"
                        marginTop={4}
                        marginBottom={2}
                      >
                        {item.title}
                      </Text>

                      {renderRichText(item.description, options)}
                    </GridItem>
                  ))}
              </Grid>
            ) : null}
          </AccordionPanel>
        </AccordionItem>
      ))}
    </Accordion>
  );
}
